/** NHIS 트랜잭션 상태 */
export type NhisStatus =
  | 'PENDING'
  | 'AUTH_REQUESTED'
  | 'FETCHED'
  | 'DELIVERED'
  | 'FAILED'
  | 'EXPIRED'
  | 'CANCELLED';

/** 인증 방식 (간편인증 / 휴대폰 본인인증) */
export type AuthMethodType = 'SIMPLE' | 'PHONE_VERIFY';

/** 동의 페이지 정보 (GET /nhis/consent/:token 응답) */
export interface ConsentInfo {
  pharmacistName: string;
  patientName: string;
  status: NhisStatus;
  errorMessage?: string;
  /** 인증 방식 (DB에 저장된 경우에만 포함) */
  authMethodType?: AuthMethodType;
  /** 간편인증 수단 loginType 코드 (authMethodType="SIMPLE"일 때만) */
  authMethod?: string;
  /** 마스킹된 환자 전화번호 (예: "010-****-5678"). 백엔드에서 제공 시 표시. */
  maskedPhoneNumber?: string;
  /**
   * 케어링노트(파트너) 경로 여부. true면 동의 6항목(늘픔가치 제3자 제공 포함)을 렌더한다.
   * 구버전 백엔드는 이 필드를 주지 않으므로(옵셔널) 없으면 기존 5항목으로 동작한다.
   */
  isPartner?: boolean;
  /**
   * 파트너별 동의 화면 프로필. 백엔드가 파트너 설정으로 내려 준다(spec D28).
   * - "kmrs"       → K-MRS(서울시약사회) 제3자 제공판
   * - "caringnote" → 기존 늘픔가치판
   * - 그 밖·null·미제공(구버전 백엔드) → isPartner 불리언 분기로 폴백(배포 순서 공존)
   */
  consentProfile?: string | null;
  /**
   * DELIVERED 시 심평원에서 전달된 처방 건수. 0이면 "조회 0건 종결"이다.
   * 새로고침으로 메모리 state가 날아가도 서버 값으로 0건/정상 전달을 구분하는 유일한 근거.
   * 구버전 백엔드는 이 필드를 주지 않으므로(옵셔널) 없으면 기존 동작(정상 전달)으로 본다.
   */
  collectedCount?: number;
}

/** NHIS 요청 상태 (약사용, GET /nhis/request/:transactionId 응답) */
/** DELIVERED 상태에서 반환되는 처방전 저장 결과 */
export interface NhisDeliveryResult {
  total: number;
  new: number;
  duplicate: number;
}

export interface NhisTransactionStatus {
  transactionId: string;
  token?: string;
  status: NhisStatus;
  patientId?: number; // 목록 응답에 포함 (대시보드 환자 상세 이동용)
  patientName: string;
  createdAt: string;
  deliveredAt?: string;
  prescriptionGroupIds?: string[];
  errorMessage?: string;
  deliveryResult?: NhisDeliveryResult | null; // DELIVERED 시 저장 결과
}

/** 인증 수단 (간편인증 개별 수단 또는 PHONE_VERIFY) */
export interface AuthMethod {
  code: string;      // SIMPLE: loginType 코드, PHONE_VERIFY: 'PHONE_VERIFY' 식별자
  name: string;      // 표시명
  icon?: string;     // 아이콘
  disabled: boolean; // Phase 1에서 미지원 시 true
  authMethodType: AuthMethodType;
  /** PHONE_VERIFY 등 부제목이 필요한 경우 */
  subtitle?: string;
  disabledLabel?: string; // 비활성 시 표시 문구
}

/** 통신사 선택 옵션 */
export interface TelecomOption {
  code: string;
  name: string;
}

/**
 * PASS 간편인증 통신사 3종.
 * 값은 현재 프로덕션 동작 유지를 위해 변경하지 않음.
 * 가이드 5.2항의 PASS 매핑(1/2/3)과 다른 점은 별도 이슈로 파킹됨.
 */
export const PASS_TELECOM_OPTIONS: TelecomOption[] = [
  { code: '0', name: 'SKT' },
  { code: '1', name: 'KT' },
  { code: '2', name: 'LGU+' },
];

/** PHONE_VERIFY 통신사 6종 (알뜰폰 포함). 가이드 5.1항 기준. */
export const PHONE_VERIFY_TELECOM_OPTIONS: TelecomOption[] = [
  { code: '0', name: 'SKT' },
  { code: '1', name: 'KT' },
  { code: '2', name: 'LG U+' },
  { code: '3', name: 'SKT 알뜰폰' },
  { code: '4', name: 'KT 알뜰폰' },
  { code: '5', name: 'LG U+ 알뜰폰' },
];

/**
 * 통신사 선택 필수 여부.
 * - PHONE_VERIFY: 항상 필수
 * - SIMPLE + PASS(code '10'): 필수
 * - 그 외 SIMPLE: 불필요
 */
export const REQUIRES_TELECOM = (method: AuthMethod): boolean => {
  return method.authMethodType === 'PHONE_VERIFY' || method.code === '10';
};

/**
 * 사용 가능한 인증 수단 목록.
 * - 최상단: 휴대폰 본인인증 (고령층 접근성 강화, 2026-04-14 A2 결정)
 * - 이하: 간편인증 5종 (카카오/PASS/네이버/토스/삼성패스)
 */
export const AUTH_METHODS: AuthMethod[] = [
  {
    code: 'PHONE_VERIFY',
    name: '휴대폰 본인인증',
    icon: '/images/auth/phone.svg',
    disabled: false,
    authMethodType: 'PHONE_VERIFY',
  },
  { code: '2',  name: '카카오톡',   icon: '/images/auth/kakao.svg',       disabled: false, authMethodType: 'SIMPLE' },
  { code: '10', name: '통신사PASS', icon: '/images/auth/pass.svg',        disabled: false, authMethodType: 'SIMPLE' },
  { code: '0',  name: '네이버',     icon: '/images/auth/naver.svg',       disabled: false, authMethodType: 'SIMPLE' },
  { code: '6',  name: '토스',       icon: '/images/auth/toss.svg',        disabled: false, authMethodType: 'SIMPLE' },
  { code: '8',  name: '삼성패스',   icon: '/images/auth/samsungpass.svg', disabled: false, authMethodType: 'SIMPLE' },
];

// --- 동의 관련 타입 ---

export type ConsentItemType =
  | 'PERSONAL_INFO'
  | 'RESIDENT_NUMBER'
  | 'SENSITIVE_INFO'
  | 'AI_OVERSEAS_TRANSFER'
  | 'THIRD_PARTY'
  | 'CARINGNOTE_THIRD_PARTY'
  /** K-MRS(서울시약사회) 경로 전용 6번 — 범용 제3자 제공 키(제출 키가 CARINGNOTE_THIRD_PARTY와 다르다) */
  | 'PARTNER_THIRD_PARTY';

/**
 * 동의 화면 판 식별자 — ConsentPage가 ConsentInfo(consentProfile·isPartner)를 정규화한 내부 분기값.
 * 'default'는 비파트너 5항목판, 'caringnote'는 늘픔가치 6항목판, 'kmrs'는 K-MRS 6항목판.
 */
export type ConsentProfile = 'kmrs' | 'caringnote' | 'default';

/** 동의 항목별 동의 여부 (API 전송용). 6번(케어링노트)은 파트너 경로에서만 존재한다. */
export interface ConsentStatus {
  PERSONAL_INFO: boolean;
  RESIDENT_NUMBER: boolean;
  SENSITIVE_INFO: boolean;
  AI_OVERSEAS_TRANSFER: boolean;
  THIRD_PARTY: boolean;
  /** 케어링노트(늘픔가치) 제3자 제공 — 늘품 파트너 경로 전용 6번 항목 */
  CARINGNOTE_THIRD_PARTY?: boolean;
  /** K-MRS(서울시약사회·서울대) 제3자 제공 — K-MRS 파트너 경로 전용 6번 항목 */
  PARTNER_THIRD_PARTY?: boolean;
}

/** 동의 객체 (API 전송 단위) */
export interface Consent {
  status: ConsentStatus;
  version: string;
}

/** 체크박스 UI 설정용 */
export interface ConsentItemConfig {
  id: ConsentItemType;
  label: string;
  required: boolean;
}

/**
 * NHIS 에러 코드별 사용자 표시 메시지.
 * 실제 백엔드 enum 및 가이드 6.2항 (2026-04-14)과 정합.
 * 7117은 서버의 동적 message가 더 구체적이므로 nhisService.ts catch 블록에서 서버 message 우선.
 */
export const NHIS_ERROR_MESSAGES: Record<number, string> = {
  7100: '요청을 찾을 수 없습니다.',
  7101: '요청이 만료되었습니다. (24시간) 약사에게 다시 요청해주세요.',
  7102: '유효하지 않은 링크입니다. 약사에게 확인해주세요.',
  7103: '현재 상태에서는 이 작업을 수행할 수 없습니다.',
  7110: '인증에 실패했습니다. 정보를 확인하고 다시 시도해주세요.',
  7111: '시간이 초과되었습니다. 다시 시도해주세요.',
  7112: '데이터 조회에 실패했습니다. 잠시 후 다시 시도해주세요.',
  7113: '서비스 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
  7114: '현재 건강보험심사평가원 서비스를 이용할 수 없습니다.',
  7115: '아직 인증이 완료되지 않았습니다. 인증을 완료해주세요.',
  7116: '인증 시간이 만료되었습니다. 처음부터 다시 시도해주세요.',
  7117: '입력 정보를 확인해주세요.',
  7120: '약품 정보를 조회하지 못했습니다. 잠시 후 다시 시도해주세요.',
  7121: '처방전 전달에 실패했습니다. 다시 시도해주세요.',
};

/** Octover detailCode별 환자 친화적 에러 메시지 (원인 설명) */
export const NHIS_DETAIL_MESSAGES: Record<string, string> = {
  '1000482': '인증 시간이 초과되었습니다.',
  '1000483': '입력된 정보가 일치하지 않습니다.',
  '1000485': '인증 진행 중 문제가 발생했습니다.',
  '1000486': '문자인증이 차단된 번호입니다.',
};

/** Octover detailCode별 환자 행동 안내 (토스트에서 <br/>로 줄바꿈하여 표시) */
export const NHIS_DETAIL_ACTIONS: Record<string, string> = {
  '1000482': '약사님에게 새로운 요청을 해주세요.',
  '1000483': '약사님에게 이름과 전화번호 확인을 요청해주세요.',
  '1000485': '약사님에게 새로운 요청을 해주세요.',
  '1000486': '통신사 차단 해제 후 약사님에게 다시 요청해주세요.',
};
